// --- UPDATED FILE: packages/api-client/src/services/public/sprintService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type {
  ServiceResponse,
  SprintAnalyticsResponse,
  PublicSprintDashboardResponse,
  UUID,
  Sprint, // <-- ADDED IMPORT for the Sprint type
} from '@dopomogai/types';

// The prefix for all public sprint-related API calls.
const PUBLIC_SPRINTS_PREFIX = '/sprints';

/**
 * A collection of service functions for interacting with the public-facing Sprint API endpoints.
 */
export const sprintService = {
  /**
   * --- NEW FUNCTION ---
   * Fetches a list of all public sprints across all projects.
   * This corresponds to the `GET /v1/sprints` endpoint.
   */
  listSprints: (): ServiceResponse<Sprint[]> => {
    return getApiClient().get<Sprint[]>(PUBLIC_SPRINTS_PREFIX)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing all public sprints'));
  },
  
  /**
   * Fetches the complete data package for a public sprint dashboard.
   * This is the primary, most efficient endpoint for any sprint detail view.
   * It returns the sprint board data AND the necessary language pack.
   * @param sprintId The UUID of the sprint to fetch.
   * @param locale The desired language for the translations.
   */
  getSprintDashboard: (sprintId: UUID, locale: string): ServiceResponse<PublicSprintDashboardResponse> => {
    return getApiClient().get<PublicSprintDashboardResponse>(`${PUBLIC_SPRINTS_PREFIX}/${sprintId}/dashboard`, { params: { locale } })
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching dashboard for sprint '${sprintId}'`));
  },
  
  /**
   * Fetches project management analytics (burndown data) for a sprint.
   * @param sprintId The UUID of the sprint.
   * @param metric The unit of measurement ('tasks' or 'points').
   */
  getSprintAnalytics: (sprintId: UUID, metric: 'tasks' | 'points'): ServiceResponse<SprintAnalyticsResponse> => {
    // Note: The backend endpoint for this was moved under /v1/analytics/sprint/{sprint_id}
    return getApiClient().get<SprintAnalyticsResponse>(`/v1/analytics/sprint/${sprintId}`, { params: { metric } })
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching analytics for sprint '${sprintId}'`));
  },
};